import { Component } from '@theme/component';
import { prefersReducedMotion, debounce, isMobileBreakpoint } from '@theme/utilities';

/** @type {Promise<any> | undefined} */
let pixiPromise;

/**
 * Lazily loads the vendored PIXI.js build the first time a <canvas-slider>
 * needs it. Shared across every instance on the page via a single promise.
 *
 * @param {string} src
 * @returns {Promise<any>}
 */
function loadPixi(src) {
  if (window.PIXI) return Promise.resolve(window.PIXI);
  if (!pixiPromise) {
    pixiPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => (window.PIXI ? resolve(window.PIXI) : reject(new Error('PIXI did not initialize')));
      script.onerror = () => reject(new Error('Failed to load PIXI.js'));
      document.head.append(script);
    });
  }
  return pixiPromise;
}

function supportsWebGL() {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch {
    return false;
  }
}

/**
 * Waits for an already-in-DOM <img> to finish loading. Building PIXI
 * textures from real DOM elements (rather than handing PIXI a URL string)
 * avoids PIXI's own URL resolver entirely, which doesn't understand
 * Shopify's protocol-relative asset URLs.
 *
 * @param {HTMLImageElement} img
 * @returns {Promise<HTMLImageElement>}
 */
function waitForImage(img) {
  if (img.complete && img.naturalWidth) return Promise.resolve(img);
  return new Promise((resolve) => {
    img.addEventListener('load', () => resolve(img), { once: true });
    img.addEventListener('error', () => resolve(img), { once: true });
  });
}

/**
 * @param {string} src
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
  });
}

/**
 * @param {number} n
 * @returns {string}
 */
function pad(n) {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * @param {number} t
 * @returns {number}
 */
/**
 * @param {number} t
 * @returns {number}
 */
const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

/**
 * @typedef {Object} SliderResource
 * @property {'image' | 'video'} type
 * @property {HTMLImageElement | null} imageEl
 * @property {HTMLVideoElement | null} videoEl
 * @property {'cover' | 'contain'} fit
 * @property {HTMLElement} el
 */

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} stage
 * @property {HTMLElement[]} slides
 * @property {HTMLElement} [prevButton]
 * @property {HTMLElement} [nextButton]
 * @property {HTMLButtonElement} [playToggle]
 * @property {HTMLElement} [progress]
 * @property {HTMLElement} [progressBar]
 * @property {HTMLElement} [counterCurrent]
 * @property {HTMLElement} [counterTotal]
 * @property {HTMLElement} [status]
 */

/**
 * A hero slideshow that renders its slides to a WebGL <canvas> via PIXI.js
 * and cross-fades between them with a mask-driven liquid displacement
 * effect (the technique used by sites like int.toga.jp).
 *
 * Progressively enhanced: the markup is a fully working, accessible CSS
 * cross-fade slideshow (real <img>/<video>/<a>) on its own. Canvas rendering
 * is layered on top only once PIXI has loaded and WebGL is confirmed
 * available, and is skipped entirely when the visitor prefers reduced
 * motion.
 *
 * @extends {Component<Refs>}
 */
export class CanvasSlider extends Component {
  requiredRefs = ['stage'];

  #index = 0;
  /** @type {SliderResource[]} */
  #resources = [];
  /** @type {any} PIXI.Application instance */
  #app = null;
  /** @type {any[]} PIXI.Container instances, one per slide */
  #containers = [];
  /** @type {any[]} PIXI.Sprite instances, one per slide */
  #sprites = [];
  /** @type {any[]} PIXI.Sprite instances used as displacement maps, one per slide */
  #displacementSprites = [];
  /** @type {any[]} PIXI.DisplacementFilter instances, one per slide */
  #displacementFilters = [];
  // Serializes calls to #transitionCanvas so rapid slide changes (fast arrow
  // clicks, a short autoplay delay) queue up instead of running concurrently
  // and fighting over the same sprites.
  /** @type {Promise<void>} */
  #transitionQueue = Promise.resolve();
  #destroyed = false;
  #visible = true;
  #userPaused = false;
  #autoplay = false;
  #autoplaySpeed = 5000;
  #transitionSpeed = 1000;
  #strength = 60;
  #canvasWidth = 1;
  #canvasHeight = 1;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  async connectedCallback() {
    super.connectedCallback();
    this.#destroyed = false;

    this.#autoplay = this.dataset.autoplay === 'true';
    this.#autoplaySpeed = (parseFloat(this.dataset.autoplaySpeed || '') || 5) * 1000;
    this.#transitionSpeed = parseInt(this.dataset.transitionSpeed || '', 10) || 1000;
    this.#strength = parseInt(this.dataset.displacementStrength || '', 10) || 60;

    this.#resources = this.#collectResources();
    const total = this.#resources.length;

    const { counterCurrent, counterTotal } = this.refs;
    if (counterTotal) counterTotal.textContent = pad(total);
    if (counterCurrent) counterCurrent.textContent = pad(1);

    if (total <= 1) {
      this.classList.add('canvas-slider--single');
      return;
    }

    this.#setupEventListeners();
    this.#pauseInactiveVideos();

    if (prefersReducedMotion() || !supportsWebGL() || !this.dataset.pixiSrc) {
      this.#setupFallback();
      return;
    }

    try {
      await this.#initCanvas();
    } catch (error) {
      if (this.#destroyed) return;
      console.warn('[canvas-slider] Falling back to CSS slideshow:', error);
      this.#destroyCanvas();
      this.#setupFallback();
    }
  }

  updatedCallback() {
    super.updatedCallback();
    // The theme editor can morph this section's markup in place (block
    // add/remove/reorder). Rebuilding from scratch keeps the PIXI scene in
    // sync with the DOM rather than trying to diff it.
    this.#destroy();
    this.#index = 0;
    this.#containers = [];
    this.#sprites = [];
    this.#displacementSprites = [];
    this.#displacementFilters = [];
    this.classList.remove(
      'canvas-slider--canvas-active',
      'canvas-slider--ready',
      'canvas-slider--fallback',
      'canvas-slider--single',
      'canvas-slider--paused'
    );
    this.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroy();
  }

  prev() {
    this.#advance(-1);
  }

  next() {
    this.#advance(1);
  }

  toggleAutoplay() {
    this.#userPaused = !this.#userPaused;
    const { playToggle } = this.refs;
    this.classList.toggle('canvas-slider--paused', this.#userPaused);
    playToggle?.setAttribute('aria-pressed', String(this.#userPaused));
    playToggle?.setAttribute('aria-label', this.#userPaused ? 'Play slideshow' : 'Pause slideshow');
    if (this.#userPaused) {
      this.#pauseAutoplay();
    } else {
      this.#startAutoplay({ restart: false });
    }
  }

  /**
   * @param {number} direction
   */
  #advance(direction) {
    const nextIndex = (this.#index + direction + this.#resources.length) % this.#resources.length;
    this.#goTo(nextIndex, direction);
  }

  /**
   * @param {number} index
   * @param {number} direction
   */
  #goTo(index, direction = 1) {
    if (index === this.#index) return;
    const from = this.#index;
    this.#index = index;
    this.#syncDom(index);
    if (this.#app) {
      this.#transitionQueue = this.#transitionQueue.then(() => (this.#destroyed ? undefined : this.#transitionCanvas(from, index, direction)));
    }
    this.#restartAutoplay();
  }

  /**
   * @returns {SliderResource[]}
   */
  #collectResources() {
    return (this.refs.slides || [])
      .map((slide) => {
        /** @type {'image' | 'video'} */
        const type = slide.dataset.type === 'video' ? 'video' : 'image';
        const imageEl = type === 'image' ? slide.querySelector('img') : null;
        const videoEl = type === 'video' ? slide.querySelector('video') : null;
        /** @type {'cover' | 'contain'} */
        const fit = slide.dataset.fit === 'contain' ? 'contain' : 'cover';
        return { type, imageEl, videoEl, fit, el: slide };
      })
      .filter((resource) => resource.imageEl || resource.videoEl);
  }

  #pauseInactiveVideos() {
    this.refs.slides?.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === this.#index) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  /**
   * @param {number} index
   */
  #syncDom(index) {
    const { slides, counterCurrent } = this.refs;
    slides?.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      const link = slide.querySelector('.canvas-slider__slide-link');
      if (link instanceof HTMLElement) {
        link.tabIndex = active ? 0 : -1;
        link.setAttribute('aria-hidden', String(!active));
      }
      const video = slide.querySelector('video');
      if (video) {
        if (active) {
          video.currentTime = 0;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    });
    if (counterCurrent) counterCurrent.textContent = pad(index + 1);
    this.#announce(index);
  }

  /**
   * @param {number} index
   */
  #announce(index) {
    const { status, slides } = this.refs;
    if (!status) return;
    const label = slides?.[index]?.querySelector('.canvas-slider__slide-link')?.textContent?.trim();
    status.textContent = `Slide ${index + 1} of ${this.#resources.length}${label ? `: ${label}` : ''}`;
  }

  #activateCurrentLink() {
    const link = this.refs.slides?.[this.#index]?.querySelector('.canvas-slider__slide-link');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.target === '_blank') {
      window.open(link.href, '_blank', 'noopener');
    } else {
      window.location.assign(link.href);
    }
  }

  #setupEventListeners() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.refs.stage?.addEventListener('pointerdown', this.#handlePointerDown, { signal, passive: true });
    this.refs.progressBar?.addEventListener(
      'animationend',
      () => {
        if (!this.#userPaused) this.next();
      },
      { signal }
    );
  }

  #handleKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  };

  #handlePointerDown = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const stage = this.refs.stage;
    if (!stage) return;

    const startX = event.clientX;
    const startY = event.clientY;
    // Undetermined until the pointer has moved enough to tell a horizontal
    // swipe attempt apart from a vertical scroll attempt.
    /** @type {'x' | 'y' | null} */
    let axis = null;

    const controller = new AbortController();
    // Explicitly passive, and no setPointerCapture anywhere in here: this
    // needs to stay a plain, non-claiming observer of the gesture so the
    // browser is completely free to treat a vertical drag as a normal page
    // scroll on its own, using only touch-action: pan-y (see stylesheet) to
    // arbitrate. Capturing the pointer — even deferred until horizontal
    // intent looked confirmed — was still enough to suppress native scroll
    // on real devices; plain uncaptured listeners are the pattern
    // production swipe libraries actually rely on for this.
    //
    // Listening on the document rather than just the stage element means
    // we keep tracking the gesture even if the finger drifts off the
    // canvas mid-swipe, without needing capture to do it.
    const opts = { signal: controller.signal, passive: true };

    document.addEventListener(
      'pointermove',
      (/** @type {PointerEvent} */ e) => {
        if (axis !== null) return;
        const dx = Math.abs(e.clientX - startX);
        const dy = Math.abs(e.clientY - startY);
        if (dx < 10 && dy < 10) return;
        axis = dx > dy ? 'x' : 'y';
      },
      opts
    );

    const finish = (/** @type {PointerEvent} */ e) => {
      controller.abort();
      const dx = e.clientX - startX;
      if (axis === 'x' && Math.abs(dx) > 40) {
        dx < 0 ? this.next() : this.prev();
      } else if (e.type === 'pointerup' && axis === null) {
        // The canvas sits above the real slide links and intercepts pointer
        // events itself, so a tap that wasn't a swipe needs to be forwarded
        // to the active slide's link manually. Only a genuine tap (no
        // meaningful movement in either direction) should do this.
        this.#activateCurrentLink();
      }
    };

    document.addEventListener('pointerup', finish, opts);
    document.addEventListener('pointercancel', finish, opts);
  };

  /**
   * CSS-only crossfade slideshow. Used when reduced motion is requested,
   * WebGL isn't available, or the PIXI build failed to load.
   */
  #setupFallback() {
    this.classList.add('canvas-slider--fallback');
    if (prefersReducedMotion()) {
      this.#autoplay = false;
      this.refs.progress?.classList.add('hidden');
      this.refs.playToggle?.classList.add('hidden');
      return;
    }
    this.#startAutoplay();
    this.#observeVisibility();
  }

  async #initCanvas() {
    const stage = this.refs.stage;
    if (!stage || !this.dataset.pixiSrc) throw new Error('Missing stage or PIXI source');

    const PIXI = await loadPixi(this.dataset.pixiSrc);
    if (this.#destroyed) return;
    PIXI.utils?.skipHello?.();

    const [, maskImg] = await Promise.all([
      Promise.all(this.#resources.map((r) => (r.imageEl ? waitForImage(r.imageEl) : Promise.resolve()))),
      this.dataset.maskSrc ? loadImage(this.dataset.maskSrc) : Promise.resolve(null),
    ]);
    if (this.#destroyed || !maskImg) return;

    // Matches the container's actual aspect ratio rather than a fixed
    // internal resolution: a fixed ratio needs its own crop to fill the
    // section (object-fit: cover on the canvas), and that crop combined
    // with the sprite's own cover-fit inside the canvas compounds into
    // unpredictable cropping whenever the section's on-screen aspect ratio
    // doesn't match the fixed one. Sizing 1:1 with the container means
    // there's only ever one crop happening, not two fighting each other.
    this.#canvasWidth = stage.clientWidth || 1;
    this.#canvasHeight = stage.clientHeight || 1;

    const app = new PIXI.Application({
      width: this.#canvasWidth,
      height: this.#canvasHeight,
      backgroundAlpha: 0,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
    });
    this.#app = app;
    stage.appendChild(app.view);
    app.view.setAttribute('aria-hidden', 'true');
    // PIXI's event system calls preventDefault() on every pointer/touch
    // interaction over the canvas by default, which blocks native scrolling
    // no matter what touch-action says in CSS (a JS-level preventDefault
    // always wins). We handle our own gestures, so this needs to be off.
    if (app.renderer.events) app.renderer.events.autoPreventDefault = false;
    // PIXI's event system also sets touch-action: none directly on the
    // canvas as an inline style when it attaches its own listeners — this
    // happens *after* the stylesheet is parsed and an inline style always
    // wins over an external CSS rule no matter its specificity, so the
    // touch-action: pan-y in the stylesheet was being silently overwritten
    // and never actually took effect. Overriding the inline style here,
    // after PIXI has set it, is the only place this can actually be fixed.
    app.view.style.touchAction = 'pan-y';

    this.#resources.forEach((resource, i) => {
      const container = new PIXI.Container();
      const texture =
        resource.type === 'video' && resource.videoEl
          ? PIXI.Texture.from(resource.videoEl)
          : resource.imageEl
            ? PIXI.Texture.from(resource.imageEl)
            : null;
      if (!texture) return;

      const sprite = new PIXI.Sprite(texture);
      sprite.anchor.set(0.5);
      sprite.alpha = i === this.#index ? 1 : 0;
      container.renderable = i === this.#index;
      container.addChild(sprite);

      const displacementSprite = new PIXI.Sprite(PIXI.Texture.from(maskImg));
      displacementSprite.anchor.set(0.5);
      displacementSprite.renderable = false;
      displacementSprite.texture.baseTexture.wrapMode = PIXI.WRAP_MODES.REPEAT;

      const filter = new PIXI.DisplacementFilter(displacementSprite);
      filter.padding = this.#strength + 20;
      filter.scale.set(0);
      container.filters = [filter];

      app.stage.addChild(displacementSprite);
      app.stage.addChild(container);

      this.#containers[i] = container;
      this.#sprites[i] = sprite;
      this.#displacementSprites[i] = displacementSprite;
      this.#displacementFilters[i] = filter;
    });

    this.#layout();
    this.#resizeObserver = new ResizeObserver(
      debounce(() => {
        if (!this.#app || this.#destroyed) return;
        this.#canvasWidth = stage.clientWidth || 1;
        this.#canvasHeight = stage.clientHeight || 1;
        this.#app.renderer.resize(this.#canvasWidth, this.#canvasHeight);
        this.#layout();
      }, 150)
    );
    this.#resizeObserver.observe(stage);

    this.classList.add('canvas-slider--canvas-active');
    requestAnimationFrame(() => this.classList.add('canvas-slider--ready'));

    this.#observeVisibility();
    this.#startAutoplay();
  }

  #layout() {
    if (!this.#app) return;
    const width = this.#canvasWidth;
    const height = this.#canvasHeight;
    const cx = width / 2;
    const cy = height / 2;

    this.#sprites.forEach((sprite, i) => {
      const texture = sprite?.texture;
      if (!texture?.width || !texture?.height) return;
      // "Contain" looks right with room to breathe on desktop, but on a
      // narrow phone screen it leaves a small image stranded in a lot of
      // empty space — cropping to fill reads better there regardless of
      // the section setting.
      const wantsContain = this.#resources[i]?.fit === 'contain' && !isMobileBreakpoint();
      const fit = wantsContain ? Math.min : Math.max;
      // The 3% overscan only matters for "cover": it gives strong
      // displacement a little slack to pull from at the edges instead of
      // tearing. "Contain" already leaves the whole image visible with
      // margin around it, so it doesn't need (or want) to be scaled past
      // an exact fit.
      const scale = fit(width / texture.width, height / texture.height) * (fit === Math.max ? 1.03 : 1);
      sprite.width = texture.width * scale;
      sprite.height = texture.height * scale;
      sprite.position.set(cx, cy);
    });

    this.#displacementSprites.forEach((sprite) => {
      if (!sprite?.texture?.width || !sprite.texture.height) return;
      const tile = Math.max(sprite.texture.width, sprite.texture.height) * 1.5;
      sprite.width = tile;
      sprite.height = tile;
      sprite.position.set(cx, cy);
    });

  }

  /**
   * @param {number} from
   * @param {number} to
   * @param {number} direction
   */
  async #transitionCanvas(from, to, direction) {
    const outSprite = this.#sprites[from];
    const inSprite = this.#sprites[to];
    const outContainer = this.#containers[from];
    const inContainer = this.#containers[to];
    const outFilter = this.#displacementFilters[from];
    const inFilter = this.#displacementFilters[to];
    if (!outSprite || !inSprite || !outContainer || !inContainer || !outFilter || !inFilter) {
      return;
    }

    outContainer.renderable = true;
    inContainer.renderable = true;

    const centerX = this.#canvasWidth / 2;
    const centerY = this.#canvasHeight / 2;
    // Negated so "next" (direction 1) enters from the right and exits to
    // the left, rather than the reverse.
    const shift = -(this.#canvasWidth * 0.02 * direction);

    inSprite.alpha = 0;
    inSprite.position.set(centerX - shift, centerY);

    // Driven by PIXI's own ticker rather than a separate requestAnimationFrame
    // loop: two independent rAF loops (one rendering, one updating sprite
    // properties) can interleave out of order, so a property change can land
    // one frame late relative to what just got rendered. Updating from
    // inside the same ticker PIXI renders from keeps them in lockstep.
    await new Promise((resolve) => {
      const ticker = this.#app.ticker;
      const start = performance.now();
      const onTick = () => {
        if (this.#destroyed) {
          ticker.remove(onTick);
          resolve(undefined);
          return;
        }
        const linear = this.#transitionSpeed > 0 ? Math.min((performance.now() - start) / this.#transitionSpeed, 1) : 1;
        const eased = easeInOutQuad(linear);
        const bump = Math.sin(Math.PI * linear) * this.#strength;
        outFilter.scale.set(bump);
        inFilter.scale.set(bump);
        outSprite.alpha = 1 - eased;
        outSprite.position.set(centerX + shift * eased, centerY);
        inSprite.alpha = eased;
        inSprite.position.set(centerX - shift * (1 - eased), centerY);
        if (linear >= 1) {
          ticker.remove(onTick);
          resolve(undefined);
        }
      };
      ticker.add(onTick);
    });

    if (this.#destroyed) return;

    outFilter.scale.set(0);
    inFilter.scale.set(0);
    outContainer.renderable = false;
    outSprite.position.set(centerX, centerY);
  }

  /**
   * @param {{ restart?: boolean }} [options]
   */
  #startAutoplay({ restart = true } = {}) {
    if (!this.#autoplay || this.#userPaused || this.#resources.length <= 1) return;
    const { progressBar } = this.refs;
    if (!progressBar) return;
    if (restart) {
      progressBar.style.animationDuration = `${this.#autoplaySpeed}ms`;
      progressBar.classList.remove('is-playing');
      void progressBar.offsetWidth;
      progressBar.classList.add('is-playing');
    }
    progressBar.style.animationPlayState = 'running';
  }

  #pauseAutoplay() {
    const { progressBar } = this.refs;
    if (progressBar) progressBar.style.animationPlayState = 'paused';
  }

  #restartAutoplay() {
    if (this.#userPaused || !this.#autoplay || !this.#visible) return;
    this.#startAutoplay();
  }

  #observeVisibility() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this.#visible = !!entry?.isIntersecting;
        if (this.#visible) {
          this.#app?.ticker.start();
          this.#restartAutoplay();
        } else {
          this.#app?.ticker.stop();
          this.#pauseAutoplay();
        }
      },
      { threshold: 0.1 }
    );
    this.#intersectionObserver.observe(this);

    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal: this.#abort?.signal });
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#app?.ticker.stop();
      this.#pauseAutoplay();
    } else if (this.#visible) {
      this.#app?.ticker.start();
      this.#restartAutoplay();
    }
  };

  #destroyCanvas() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#app) {
      // Textures are built from DOM <img>/<video> elements the page already
      // owns, so tell PIXI not to destroy the underlying source alongside
      // the sprites/containers.
      this.#app.destroy(true, { children: true, texture: false, baseTexture: false });
      this.#app = null;
    }
    this.classList.remove('canvas-slider--canvas-active', 'canvas-slider--ready');
  }

  #destroy() {
    this.#destroyed = true;
    this.#abort?.abort();
    this.#abort = null;
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#destroyCanvas();
  }
}

customElements.define('canvas-slider', CanvasSlider);
